import { useCallback } from 'react';
import { Button } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';

import ChevronLeftIcon from 'modules/common/components/Icons/ChevronLeftIcon';
import { toSameOriginPath } from 'modules/common/utils/safeRedirect';

/** Query param naming the wizard step Continue should land on. */
export const INTRO_NEXT_PARAM = 'next';
/** Query param naming the page Back should return to. */
export const INTRO_FROM_PARAM = 'from';

interface IntroBackButtonProps {
    fallbackPath: string;
}

export function IntroBackButton({ fallbackPath }: Readonly<IntroBackButtonProps>) {
    const history = useHistory();
    const { search } = useLocation();

    const handleClick = useCallback(() => {
        const fromPath = toSameOriginPath(new URLSearchParams(search).get(INTRO_FROM_PARAM));
        history.push(fromPath ?? fallbackPath);
    }, [history, search, fallbackPath]);

    return (
        <Button variant="outlined" startIcon={<ChevronLeftIcon />} onClick={handleClick}>
            Back
        </Button>
    );
}

interface IntroContinueButtonProps {
    /** The substeps Continue may land on, in wizard order; the first is the default. */
    stepPaths: readonly string[];
    onStepStart: (stepPath: string) => void;
}

export function IntroContinueButton({ stepPaths, onStepStart }: Readonly<IntroContinueButtonProps>) {
    const history = useHistory();
    const { search } = useLocation();

    const handleClick = useCallback(() => {
        const nextPath = new URLSearchParams(search).get(INTRO_NEXT_PARAM);
        const targetPath = nextPath && stepPaths.includes(nextPath) ? nextPath : stepPaths[0];
        onStepStart(targetPath);
        history.push(targetPath);
    }, [search, stepPaths, onStepStart, history]);

    return (
        <Button variant="contained" onClick={handleClick}>
            Continue
        </Button>
    );
}
